/**
 * SSOT-only LDS order price trace — display chain + prune audit metadata.
 * Does not compute prices; reads persisted ourPrice / order fields only.
 */
import { resolveLdsOrderFinalPriceBreakdown } from "../../../utils/extractLdsOrderFinalPriceIls";
import { resolveLdsOrderPageDisplayTotalSsot } from "../../../utils/orders/ldsOrderPageDisplayTotalSsot";
import { resolveLdsPartialPaymentDisplayBalance } from "../../../utils/orders/resolveLdsPartialPaymentDisplayBalance";
import { coerceCampaignClientsInfo } from "../../../utils/orders/campaignOrderContext.js";

export const TRACE_FIELD_PRUNE_AUDIT_TAG = "[TRACE_FIELD_PRUNE_AUDIT]";

const MISSING = "לא קיים";

/** @typedef {"ACTIVE"|"DEPRECATED"|"UNUSED"|"DEBUG_ONLY"} TraceFieldClass */
/** @typedef {"KEEP_IN_TRACE"|"REMOVE_FROM_TRACE"} TraceRemovalDecision */

/**
 * Static registry: every field that ever appeared in the LDS price trace table.
 * removalDecision applies to TRACE UI only — not to business code or APIs.
 * @type {Array<{
 *   field: string,
 *   producer: string,
 *   consumer: string,
 *   isUsed: boolean,
 *   classification: TraceFieldClass,
 *   removalDecision: TraceRemovalDecision,
 *   reason: string,
 * }>}
 */
export const LDS_ORDER_PRICE_TRACE_FIELD_REGISTRY = [
  {
    field: "initialPrice",
    producer: "pricing pipeline → ourPrice.initialPrice",
    consumer: "HR margin chain, order persist",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "KEEP_IN_TRACE",
    reason: "SSOT supplier base (₪)",
  },
  {
    field: "ldsPrice",
    producer: "legacy LDS fare API",
    consumer: "vacation search cards (non-order)",
    isUsed: false,
    classification: "DEPRECATED",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Not in order resolved-price chain",
  },
  {
    field: "netPrice",
    producer: "supplier XML / ourTotalFare",
    consumer: "admin margin debug only",
    isUsed: false,
    classification: "DEBUG_ONLY",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Supplier raw; not used in FE display total SSOT",
  },
  {
    field: "originalCurrency (origCurrency)",
    producer: "orig_currency pricing branch",
    consumer: "USD display lines (hotel/profile)",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Parallel FX path; not in ILS resolved chain",
  },
  {
    field: "fx_rate / currencyRate",
    producer: "orig_currency snapshot",
    consumer: "USD conversion UI",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Not in ILS order-page SSOT chain",
  },
  {
    field: "hrCommissionPercent",
    producer: "unified pricing engine",
    consumer: "pricing audit / engine input",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Percent metadata; chain uses hrCommission amount",
  },
  {
    field: "unifiedHrMarginPercent",
    producer: "unified HR margin service",
    consumer: "pricing engine audit",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Duplicate of percent metadata; amount in hrCommission",
  },
  {
    field: "unifiedHrMarginSourceFeatureKey",
    producer: "HR plan resolver",
    consumer: "pricing audit steps",
    isUsed: true,
    classification: "DEBUG_ONLY",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Audit metadata only",
  },
  {
    field: "hrCommission",
    producer: "pricing pipeline",
    consumer: "priceWithCommission, persist",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "KEEP_IN_TRACE",
    reason: "SSOT HR margin amount",
  },
  {
    field: "unifiedHrMargin",
    producer: "legacy unified margin",
    consumer: "none on order page",
    isUsed: false,
    classification: "DEPRECATED",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Superseded by hrCommission on orders",
  },
  {
    field: "priceWithCommission",
    producer: "pricing pipeline",
    consumer: "handling fee base, summaries",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "KEEP_IN_TRACE",
    reason: "SSOT post-HR subtotal",
  },
  {
    field: "pricePreFinalRound",
    producer: "legacy rounding step",
    consumer: "pricing audit",
    isUsed: false,
    classification: "DEPRECATED",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Not in display total resolution",
  },
  {
    field: "handleFeeData.value",
    producer: "agent handling fee editor",
    consumer: "handleFee persist",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Duplicate of handling_fee on order row",
  },
  {
    field: "handleFee",
    producer: "ourTotalFare / legacy persist",
    consumer: "payment uplift, summaries",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Legacy alias; trace shows handling_fee only",
  },
  {
    field: "handling_fee",
    producer: "ourPrice.handling_fee / handleFee sync",
    consumer: "page total uplift, wallet split",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "KEEP_IN_TRACE",
    reason: "SSOT agent handling fee",
  },
  {
    field: "priceWithHandleFee",
    producer: "pricing pipeline",
    consumer: "credit card fee base",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "KEEP_IN_TRACE",
    reason: "SSOT post-handling subtotal",
  },
  {
    field: "creditCardFee",
    producer: "pricing pipeline",
    consumer: "priceWithCreditCardFee",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "KEEP_IN_TRACE",
    reason: "SSOT clearing fee",
  },
  {
    field: "priceWithCreditCardFee",
    producer: "pricing pipeline",
    consumer: "baggage base, payment link",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "KEEP_IN_TRACE",
    reason: "SSOT pre-baggage checkout total",
  },
  {
    field: "priceWithCommissionAndCreditCardFee",
    producer: "ourTotalFare legacy",
    consumer: "VP sidebar, legacy cards",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Parallel PWCCF; order page uses priceWithCreditCardFee",
  },
  {
    field: "baggagePrice",
    producer: "supplement / ourPrice",
    consumer: "final price breakdown",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "KEEP_IN_TRACE",
    reason: "SSOT baggage add-on",
  },
  {
    field: "public_baggage_total_ils",
    producer: "public site stamp",
    consumer: "public checkout only",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Public flow only; not order-page chain",
  },
  {
    field: "baggage_total_detected",
    producer: "resolveLdsOrderFinalPriceBreakdown",
    consumer: "display total reconcile",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "KEEP_IN_TRACE",
    reason: "SSOT detected baggage in resolved total",
  },
  {
    field: "supplement_source",
    producer: "order.supplement / order.flights.supplement",
    consumer: "TRACE baggage source",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "KEEP_IN_TRACE",
    reason: "Shows where extras were read — empty root must not hide flights.supplement",
  },
  {
    field: "baggage_included_mode",
    producer: "resolveLdsOrderFinalPriceBreakdown",
    consumer: "TRACE baggage compose mode",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "KEEP_IN_TRACE",
    reason: "Shows whether detected bags were added, already included, or skipped",
  },
  {
    field: "selectedRoom.supplement",
    producer: "extended.selectedRoom",
    consumer: "multi-room legacy",
    isUsed: false,
    classification: "DEPRECATED",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Not in single-package resolved chain",
  },
  {
    field: "supplement_checked_payload",
    producer: "order.supplement",
    consumer: "breakdown debug",
    isUsed: false,
    classification: "DEBUG_ONLY",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Raw payload; not a pricing step",
  },
  {
    field: "baggage_inclusion_mode",
    producer: "resolveLdsOrderFinalPriceBreakdown",
    consumer: "double-count guard",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Internal reconcile flag; not a price step",
  },
  {
    field: "public_base_price_to_agent_ils",
    producer: "public commission stamp",
    consumer: "public site",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Public-only; not agent order trace",
  },
  {
    field: "public_markup_ils",
    producer: "public commission stamp",
    consumer: "public site",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Public-only",
  },
  {
    field: "public_final_customer_price_ils",
    producer: "public commission stamp",
    consumer: "public site",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Public-only",
  },
  {
    field: "display_total_ils",
    producer: "BE stamp / ldsOrderPageDisplayTotalSsot",
    consumer: "billing, partial payment display",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "KEEP_IN_TRACE",
    reason: "SSOT final display total",
  },
  {
    field: "display_paid",
    producer: "payments ledger / lds_partial_payment_display",
    consumer: "remaining balance UI",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "KEEP_IN_TRACE",
    reason: "SSOT confirmed paid",
  },
  {
    field: "display_remaining",
    producer: "lds_partial_payment_display / arithmetic",
    consumer: "partial payment UI",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "KEEP_IN_TRACE",
    reason: "SSOT outstanding balance",
  },
  {
    field: "resolved_source",
    producer: "resolveLdsOrderPageDisplayTotalSsot",
    consumer: "admin trace, dev SSOT logs",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "KEEP_IN_TRACE",
    reason: "Which field won display total resolution",
  },
  {
    field: "walletCommissionPercent",
    producer: "wallet commission plan",
    consumer: "wallet split display",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Wallet UI only; not pricing chain",
  },
  {
    field: "wallet_amount",
    producer: "derived from handling_fee × percent",
    consumer: "wallet admin display",
    isUsed: false,
    classification: "DEBUG_ONLY",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Derived display; not pricing",
  },
  {
    field: "company_share",
    producer: "derived from handling_fee",
    consumer: "wallet admin display",
    isUsed: false,
    classification: "DEBUG_ONLY",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Derived display; not pricing",
  },
  {
    field: "base_before_baggage",
    producer: "resolveLdsOrderFinalPriceBreakdown",
    consumer: "legacy extractLdsOrderFinalPriceIls",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Intermediate; display uses page SSOT",
  },
  {
    field: "customer_total (extractLdsOrderFinalPriceIls)",
    producer: "extractLdsOrderFinalPriceIls",
    consumer: "legacy fallback",
    isUsed: true,
    classification: "DEPRECATED",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Superseded by display_total SSOT",
  },
  {
    field: "baggage_added_once",
    producer: "resolveLdsOrderFinalPriceBreakdown",
    consumer: "double-count guard",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Boolean guard; not a price step",
  },
  {
    field: "pricingSnapshot.final_price",
    producer: "pricing snapshot persist",
    consumer: "page SSOT fallback",
    isUsed: true,
    classification: "ACTIVE",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Shown only when populated; omitted when empty",
  },
  {
    field: "pricingAuditSteps",
    producer: "pricing engine",
    consumer: "dev pricing debug",
    isUsed: true,
    classification: "DEBUG_ONLY",
    removalDecision: "REMOVE_FROM_TRACE",
    reason: "Empty steps omitted from trace",
  },
];

const SSOT_CHAIN = [
  {
    step: "RAW API / Supplier",
    field: "initialPrice",
    source: "ourPrice / ourTotalFare",
    pick: (ctx) => ctx.op.initialPrice ?? ctx.otf.initialPrice,
  },
  {
    step: "מרווח חברה / HR",
    field: "hrCommission",
    source: "ourPrice / ourTotalFare",
    pick: (ctx) => ctx.op.hrCommission ?? ctx.otf.hrCommission,
  },
  {
    step: "מרווח חברה / HR",
    field: "priceWithCommission",
    source: "ourPrice / ourTotalFare",
    pick: (ctx) => ctx.op.priceWithCommission ?? ctx.otf.priceWithCommission,
  },
  {
    step: "דמי טיפול סוכן",
    field: "handling_fee",
    source: "ourPrice (SSOT; handleFee legacy alias)",
    pick: (ctx) =>
      ctx.op.handling_fee ??
      ctx.op.handlingFee ??
      ctx.op.handleFee ??
      ctx.otf.handleFee,
  },
  {
    step: "דמי טיפול סוכן",
    field: "priceWithHandleFee",
    source: "ourPrice / ourTotalFare",
    pick: (ctx) => ctx.op.priceWithHandleFee ?? ctx.otf.priceWithHandleFee,
  },
  {
    step: "סליקה / אשראי",
    field: "creditCardFee",
    source: "ourPrice / ourTotalFare",
    pick: (ctx) => ctx.op.creditCardFee ?? ctx.otf.creditCardFee,
  },
  {
    step: "סליקה / אשראי",
    field: "priceWithCreditCardFee",
    source: "ourPrice",
    pick: (ctx) => ctx.op.priceWithCreditCardFee,
  },
  {
    step: "כבודה / תוספות",
    field: "baggagePrice",
    source: "ourPrice / ourTotalFare / detected checked supplements",
    pick: (ctx) => {
      const fromStamp =
        ctx.op.baggagePrice ??
        ctx.otf.baggagePrice ??
        ctx.breakdown?.baggageTotalDetected;
      if (fromStamp != null && Number.isFinite(Number(fromStamp))) {
        return Number(fromStamp);
      }
      return 0;
    },
  },
  {
    step: "כבודה / תוספות",
    field: "baggage_total_detected",
    source: "resolveLdsOrderFinalPriceBreakdown",
    pick: (ctx) =>
      ctx.breakdown.baggageTotalDetected != null
        ? ctx.breakdown.baggageTotalDetected
        : 0,
  },
  {
    step: "כבודה / תוספות",
    field: "supplement_source",
    source: "resolveLdsOrderFinalPriceBreakdown",
    pick: (ctx) => ctx.breakdown.supplementSourceUsed || "none",
    money: false,
  },
  {
    step: "כבודה / תוספות",
    field: "baggage_included_mode",
    source: "resolveLdsOrderFinalPriceBreakdown",
    pick: (ctx) => ctx.breakdown.baggageIncludedMode || "none",
    money: false,
  },
  {
    step: "מחיר סופי ללקוח",
    field: "display_total_ils",
    source: "resolveLdsOrderPageDisplayTotalSsot",
    pick: (ctx) => ctx.pageSsot.displayedTotal,
  },
  {
    step: "מחיר סופי ללקוח",
    field: "display_paid",
    source: "resolveLdsPartialPaymentDisplayBalance",
    pick: (ctx) => ctx.partialBal.displayPaid,
  },
  {
    step: "מחיר סופי ללקוח",
    field: "display_remaining",
    source: "resolveLdsPartialPaymentDisplayBalance",
    pick: (ctx) => ctx.partialBal.displayRemaining,
  },
  {
    step: "מחיר סופי ללקוח",
    field: "resolved_source",
    source: "resolveLdsOrderPageDisplayTotalSsot",
    pick: (ctx) => ctx.pageSsot.source,
    money: false,
  },
];

const CAMPAIGN_PRICE_TRACE_ROWS = [
  {
    step: "עלות מלון",
    field: "מלון נטו",
    pricingKey: "hotel_net_ils",
    companionPricingKey: "hotel_net_eur",
    companionFormat: "eur",
    sourceSuffix: "hotel_net_ils (+ hotel_net_eur)",
    format: "ils",
  },
  {
    step: "מרווח HR",
    field: "מרווח HR — מלון",
    pricingKey: "hotel_markup_ils",
    sourceSuffix: "hotel_markup_ils",
    format: "ils",
  },
  {
    step: "עלות טיסה",
    field: "טיסה — עלות ספק",
    pricingKey: "flight_supplier_net_ils",
    sourceSuffix: "flight_supplier_net_ils",
    format: "ils",
  },
  {
    step: "מרווח HR",
    field: "מרווח נסתר HR — טיסה",
    pricingKey: "flight_hidden_markup_ils",
    sourceSuffix: "flight_hidden_markup_ils",
    format: "ils",
  },
  {
    step: "תוספות",
    field: "כבודה",
    pricingKey: "baggage_extras_total_ils",
    sourceSuffix: "baggage_extras_total_ils",
    format: "ils",
  },
  {
    step: "עמלת סוכן",
    field: "עמלת סוכן מובנית — ערוץ ציבורי",
    pricingKey: "flight_agent_commission_ils",
    sourceSuffix: "flight_agent_commission_ils",
    format: "ils",
    optional: true,
  },
  {
    step: "עמלת סוכן",
    field: "עמלה שנוספה ידנית",
    pricingKey: "agent_added_commission_ils",
    sourceSuffix: "agent_added_commission_ils",
    format: "ils",
    showZero: true,
    zeroLabel: "0 ₪",
  },
  {
    step: "עמלת סוכן",
    field: "סה״כ עמלת סוכן",
    pricingKey: "total_agent_commission_ils",
    sourceSuffix: "total_agent_commission_ils",
    format: "ils",
    optional: true,
  },
  {
    step: "מחיר מכירה",
    field: "מחיר נטו לסוכן",
    pricingKey: "agent_net_price_ils",
    sourceSuffix: "agent_net_price_ils",
    format: "ils",
  },
  {
    step: "מחיר מכירה",
    field: "מחיר מכירה סופי",
    pricingKey: "final_sale_price_ils",
    sourceSuffix: "final_sale_price_ils",
    format: "ils",
  },
  {
    step: "תשלום לקוח",
    field: "נגבה בפועל / סה״כ ללקוח",
    pricingKey: "charged_ils",
    sourceSuffix: "charged_ils",
    format: "ils",
    final: true,
  },
  {
    step: "מרווח HR (אדמין)",
    field: "סה״כ מרווח HR",
    pricingKey: "hr_margin_total_ils",
    sourceSuffix: "hr_margin_total_ils",
    format: "ils",
    adminOnly: true,
  },
];

function tryParseJson(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function hasPresentValue(v) {
  if (v === null || v === undefined || v === "") return false;
  if (typeof v === "number") return Number.isFinite(v);
  if (typeof v === "object") {
    if (Array.isArray(v)) return v.length > 0;
    return Object.keys(v).length > 0;
  }
  return true;
}

function fmtValue(v) {
  if (!hasPresentValue(v)) return MISSING;
  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return String(v);
}

function fmtIlsValue(v) {
  const amount = Number(v);
  if (!Number.isFinite(amount)) return MISSING;
  return `${amount.toLocaleString("he-IL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} ₪`;
}

function fmtEurValue(v) {
  const amount = Number(v);
  if (!Number.isFinite(amount)) return MISSING;
  return `${amount.toLocaleString("he-IL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} €`;
}

function positiveAmount(value) {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

/**
 * Ensure TRACE pricing exposes hotel_net_eur when possible.
 * Prefer API value; otherwise derive from hotel_net_ils ÷ eur_to_ils.
 */
export function enrichCampaignPricingHotelNetEur(pricing) {
  if (!pricing || typeof pricing !== "object") return pricing || {};
  const next = { ...pricing };
  if (positiveAmount(next.hotel_net_eur) != null) {
    next.hotel_net_eur = positiveAmount(next.hotel_net_eur);
    return next;
  }
  const ils = positiveAmount(next.hotel_net_ils ?? next.hotelNetIls);
  const rate = positiveAmount(
    next.eur_to_ils ?? next.eurToIls ?? next.eur_rate ?? next.eurRate
  );
  if (ils != null && rate != null) {
    next.hotel_net_eur = Math.round((ils / rate) * 100) / 100;
  }
  return next;
}

function finiteIlsOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.round((n + Number.EPSILON) * 100) / 100 : null;
}

/** Copy built-in public agent commission from clientsInfo when enrichment omitted it. */
function mergeCampaignAgentCommissionFromClientsInfo(pricing, order) {
  const next = { ...(pricing && typeof pricing === "object" ? pricing : {}) };
  const ci = coerceCampaignClientsInfo(order);
  if (finiteIlsOrNull(next.flight_agent_commission_ils) == null) {
    const fromCi = finiteIlsOrNull(ci.flight_agent_commission_ils);
    if (fromCi != null) next.flight_agent_commission_ils = fromCi;
  }
  if (next.flight_agent_commission_percent == null && ci.flight_agent_commission_percent != null) {
    next.flight_agent_commission_percent = ci.flight_agent_commission_percent;
  }
  const builtIn = finiteIlsOrNull(next.flight_agent_commission_ils) ?? 0;
  const added = finiteIlsOrNull(next.agent_added_commission_ils) ?? 0;
  if (builtIn > 0 || added > 0) {
    next.total_agent_commission_ils = Math.round((builtIn + added + Number.EPSILON) * 100) / 100;
  }
  return next;
}

function fmtCampaignTraceValue(spec, rawValue, pricing = {}) {
  if (spec.showZero && (rawValue === 0 || rawValue === "0")) {
    return spec.zeroLabel || "0 ₪";
  }
  if (!hasPresentValue(rawValue)) return MISSING;
  let primary = fmtValue(rawValue);
  if (spec.format === "eur") primary = fmtEurValue(rawValue);
  else if (spec.format === "ils") primary = fmtIlsValue(rawValue);

  if (spec.companionPricingKey) {
    const companionRaw = pricing[spec.companionPricingKey];
    if (hasPresentValue(companionRaw)) {
      const companion =
        spec.companionFormat === "eur"
          ? fmtEurValue(companionRaw)
          : spec.companionFormat === "ils"
            ? fmtIlsValue(companionRaw)
            : fmtValue(companionRaw);
      return `${primary} (${companion})`;
    }
  }
  return primary;
}

function resolveOurPriceBlocks(order) {
  const flightNode =
    order?.flights && typeof order.flights === "object" && !Array.isArray(order.flights)
      ? order.flights
      : null;
  const rawOp = flightNode?.ourprice ?? flightNode?.ourPrice ?? order?.ourprice ?? order?.ourPrice;
  let op =
    rawOp && typeof rawOp === "object"
      ? rawOp
      : typeof rawOp === "string"
        ? tryParseJson(rawOp)
        : null;
  if (!op || typeof op !== "object") op = {};
  const otf = op.ourTotalFare && typeof op.ourTotalFare === "object" ? op.ourTotalFare : {};
  return { op, otf };
}

/** @param {Record<string, unknown>|null|undefined} order */
export function isCampaignOrderShape(order) {
  if (!order || typeof order !== "object") return false;
  if (order.admin_campaign_hotel && typeof order.admin_campaign_hotel === "object") {
    return true;
  }
  if (order.campaign_hotel && typeof order.campaign_hotel === "object") {
    const slug = order.campaign_hotel.campaign ?? order.campaign_hotel.campaign_slug;
    if (hasPresentValue(slug)) return true;
  }
  const orderSource = String(order.orderSource ?? order.order_source ?? "").trim().toLowerCase();
  if (orderSource === "campaign") return true;
  const special = String(
    order.elig_special_recent_order ?? order.eligSpecialRecentOrder ?? ""
  )
    .trim()
    .toLowerCase();
  if (special === "campaign") return true;
  return false;
}

/** @param {Record<string, unknown>|null|undefined} order @param {{ isAdmin?: boolean }} [options] */
export function isCampaignOrderPriceTrace(order, options = {}) {
  if (!isCampaignOrderShape(order)) return false;
  if (order?.admin_campaign_hotel && typeof order.admin_campaign_hotel === "object") {
    return true;
  }
  return Boolean(options.isAdmin);
}

/**
 * Resolve campaign commercial pricing for admin trace.
 * @returns {{ pricing: Record<string, unknown>, traceSourceRoot: string, complete: boolean, campaignLabel?: string|null }|null}
 */
export function resolveCampaignPricingForTrace(order, options = {}) {
  if (!order || typeof order !== "object") return null;

  const adminCtx =
    order.admin_campaign_hotel && typeof order.admin_campaign_hotel === "object"
      ? order.admin_campaign_hotel
      : null;
  if (adminCtx?.pricing && typeof adminCtx.pricing === "object") {
    return {
      pricing: mergeCampaignAgentCommissionFromClientsInfo(
        enrichCampaignPricingHotelNetEur({ ...adminCtx.pricing }),
        order,
      ),
      traceSourceRoot: "dynamic_packages.clientsinfo → admin_campaign_hotel.pricing",
      complete: true,
      campaignLabel: adminCtx.campaign_title || adminCtx.campaign || null,
    };
  }

  if (!options.isAdmin || !isCampaignOrderShape(order)) return null;

  const ch =
    order.campaign_hotel && typeof order.campaign_hotel === "object"
      ? order.campaign_hotel
      : {};
  const { op, otf } = resolveOurPriceBlocks(order);
  const partialDisplay = order.lds_partial_payment_display;
  const partial =
    partialDisplay && typeof partialDisplay === "object" ? partialDisplay : {};

  const pickPositive = (...values) => {
    for (const value of values) {
      const amount = Number(value);
      if (Number.isFinite(amount) && amount > 0) return amount;
    }
    return null;
  };

  const chargedIls = pickPositive(
    ch.customer_total_ils,
    ch.final_sale_price_ils,
    otf.display_total_ils,
    op.display_total_ils,
    order.display_total_ils,
    partial.display_total,
    partial.displayTotal
  );

  const pricing = {
    agent_net_price_ils: pickPositive(
      otf.priceWithCommission,
      op.priceWithCommission,
      ch.final_sale_price_ils
    ),
    final_sale_price_ils: pickPositive(
      ch.final_sale_price_ils,
      ch.customer_total_ils,
      otf.priceWithCommission,
      op.priceWithCommission
    ),
    charged_ils: chargedIls,
    customer_total_ils: chargedIls,
    baggage_extras_total_ils: pickPositive(otf.baggagePrice, op.baggagePrice),
    agent_added_commission_ils: ch.is_net
      ? 0
      : pickPositive(
          op.handling_fee,
          op.handlingFee,
          op.handleFee,
          otf.handleFee,
          order.handlingfee,
          order.handling_fee
        ),
    flight_agent_commission_ils: pickPositive(
      ch.flight_agent_commission_ils,
      ch.pricing?.flight_agent_commission_ils,
    ),
  };

  if (
    !Object.values(pricing).some(
      (value) => value !== null && value !== undefined && value !== ""
    )
  ) {
    return null;
  }

  return {
    pricing: mergeCampaignAgentCommissionFromClientsInfo(pricing, order),
    traceSourceRoot:
      "campaign_hotel + LDS ourPrice (partial — full chain requires admin_campaign_hotel from API)",
    complete: false,
    campaignLabel: ch.campaign_title || ch.campaign || null,
  };
}

function buildCampaignOrderPriceTraceRows(campaignTrace, nextKey) {
  const pricing =
    campaignTrace?.pricing && typeof campaignTrace.pricing === "object"
      ? campaignTrace.pricing
      : {};
  const sourceRoot = campaignTrace?.traceSourceRoot || "admin_campaign_hotel.pricing";
  const rows = [];

  if (campaignTrace?.campaignLabel) {
    rows.push({
      key: nextKey(),
      step: "קמפיין",
      field: "שם / slug",
      value: String(campaignTrace.campaignLabel),
      source: `${sourceRoot}.campaign`,
      isCampaignMeta: true,
    });
  }

  for (const spec of CAMPAIGN_PRICE_TRACE_ROWS) {
    const raw = pricing[spec.pricingKey];
    if (!hasPresentValue(raw)) {
      if (spec.optional) continue;
      if (spec.showZero && raw === 0) {
        /* show explicit NET zero */
      } else if (!campaignTrace?.complete && spec.adminOnly) {
        continue;
      } else if (!campaignTrace?.complete && !spec.final && !spec.showZero) {
        continue;
      } else if (!hasPresentValue(raw) && !spec.showZero) {
        continue;
      }
    }

    rows.push({
      key: nextKey(),
      step: spec.step,
      field: spec.field,
      value: fmtCampaignTraceValue(spec, raw, pricing),
      source: `${sourceRoot}.${spec.sourceSuffix || spec.pricingKey}`,
      isCampaignFinal: Boolean(spec.final),
      isPartial: campaignTrace?.complete === false,
    });
  }

  return rows;
}

/**
 * @param {Record<string, unknown>|null|undefined} order
 */
export function resolveLdsOrderPriceTraceContext(order) {
  if (!order || typeof order !== "object") {
    return {
      op: {},
      otf: {},
      breakdown: {
        baggageTotalDetected: null,
        baggageSourceUsed: null,
        supplementSourceUsed: null,
        baggageIncludedMode: "none",
      },
      pageSsot: { displayedTotal: null, source: "none" },
      partialBal: {
        displayPaid: 0,
        displayRemaining: null,
      },
    };
  }

  const flightNode =
    order?.flights && typeof order.flights === "object" && !Array.isArray(order.flights)
      ? order.flights
      : null;
  const rawOp = flightNode?.ourprice ?? flightNode?.ourPrice ?? order.ourprice ?? order.ourPrice;
  let op =
    rawOp && typeof rawOp === "object"
      ? rawOp
      : typeof rawOp === "string"
        ? tryParseJson(rawOp)
        : null;
  if (!op || typeof op !== "object") op = {};

  const otf = op.ourTotalFare && typeof op.ourTotalFare === "object" ? op.ourTotalFare : {};

  return {
    op,
    otf,
    breakdown: resolveLdsOrderFinalPriceBreakdown(order),
    pageSsot: resolveLdsOrderPageDisplayTotalSsot(order, { emitTrace: false }),
    partialBal: resolveLdsPartialPaymentDisplayBalance(order),
  };
}

/**
 * @param {Record<string, unknown>|null|undefined} order
 * @param {{ isAdmin?: boolean }} [options]
 * @returns {{ rows: Array<{ key: string, step: string, field: string, value: string, source: string }>, pruneAudit: typeof LDS_ORDER_PRICE_TRACE_FIELD_REGISTRY, campaignTraceMeta?: object|null }}
 */
export function buildLdsOrderPriceTraceSsotRows(order, options = {}) {
  const rows = [];
  let k = 0;
  const nextKey = () => `ltr-ssot-${k++}`;

  const ctx = resolveLdsOrderPriceTraceContext(order);

  if (!order || typeof order !== "object") {
    rows.push({
      key: nextKey(),
      step: "סיכום",
      field: "order",
      value: MISSING,
      source: "—",
    });
    return { rows, pruneAudit: LDS_ORDER_PRICE_TRACE_FIELD_REGISTRY, campaignTraceMeta: null };
  }

  const campaignTrace = resolveCampaignPricingForTrace(order, options);
  if (campaignTrace) {
    return {
      rows: buildCampaignOrderPriceTraceRows(campaignTrace, nextKey),
      pruneAudit: LDS_ORDER_PRICE_TRACE_FIELD_REGISTRY,
      campaignTraceMeta: campaignTrace,
    };
  }

  if (isCampaignOrderShape(order) && options.isAdmin) {
    rows.push({
      key: nextKey(),
      step: "קמפיין",
      field: "campaign",
      value: "זוהתה הזמנת קמפיין — חסר admin_campaign_hotel מהשרת",
      source: "dynamic_packages.clientsinfo (admin session required)",
    });
  }

  for (const spec of SSOT_CHAIN) {
    const raw = spec.pick(ctx);
    if (!hasPresentValue(raw)) continue;
    rows.push({
      key: nextKey(),
      step: spec.step,
      field: spec.field,
      value: fmtValue(raw),
      source: spec.source,
    });
  }

  return { rows, pruneAudit: LDS_ORDER_PRICE_TRACE_FIELD_REGISTRY, campaignTraceMeta: null };
}

/**
 * @param {typeof LDS_ORDER_PRICE_TRACE_FIELD_REGISTRY} audit
 * @param {{ orderId?: string|null }} [meta]
 */
export function emitTraceFieldPruneAudit(audit, meta = {}) {
  const isDev =
    (typeof import.meta !== "undefined" && import.meta?.env?.DEV) ||
    (typeof process !== "undefined" && process?.env?.NODE_ENV !== "production");
  if (!isDev) return;
  try {
    // eslint-disable-next-line no-console
    console.info(TRACE_FIELD_PRUNE_AUDIT_TAG, {
      order_id: meta.orderId ?? null,
      kept_fields: audit.filter((e) => e.removalDecision === "KEEP_IN_TRACE").map((e) => e.field),
      removed_from_trace: audit
        .filter((e) => e.removalDecision === "REMOVE_FROM_TRACE")
        .map((e) => e.field),
      fields: audit,
    });
  } catch {
    /* ignore */
  }
}
